import React, { useState, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import aiLabApi from '../services/ai-lab';
import './AILab.css';

// ── Helpers ──
function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('fr-FR');
}
function fmtMontant(v) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v || 0);
}
function daysSince(d) {
  if (!d) return '—';
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000) + 'j';
}

// ── Composant document structuré Responsable Atelier ──
function AtelierBriefingView({ data, generatedAt }) {
  const { demandes = [], parking_factures = [], parking_demandes = [], stats = {} } = data || {};
  const dateStr = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Grouper demandes par responsable
  const demandesParResp = useMemo(() => {
    const map = {};
    demandes.forEach(d => {
      const r = d.responsable || 'Non assign\u00e9';
      if (!map[r]) map[r] = [];
      map[r].push(d);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [demandes]);

  const exportPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // En-t\u00eate
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Briefing Responsable Atelier', 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Date : ${today}`, 14, 25);
    doc.text(`Demandes en cours : ${stats.demandes_en_cours || 0}  |  Interventions du jour : ${stats.interventions_jour || 0}  |  V\u00e9hicules parking : ${stats.vehicules_parking || 0}`, 14, 31);

    let cursorY = 38;

    // Section 1 — Demandes par collaborateur
    for (const [resp, list] of demandesParResp) {
      if (cursorY + 20 > pageH - 15) { doc.addPage(); cursorY = 20; }
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`\uD83D\uDCAC ${resp} (${list.length})`, 14, cursorY);
      autoTable(doc, {
        startY: cursorY + 3,
        head: [['Client', 'T\u00e9l\u00e9phone', 'Immat', 'Sujet', 'Commentaire', 'Date', 'Appels']],
        body: list.map(d => [
          d.client || '\u2014', d.telephone || '\u2014', d.immatriculation || '\u2014',
          d.sujet || '\u2014', (d.commentaire_initial || '\u2014').substring(0, 60),
          fmtDate(d.date_demande), String(d.nb_appels || 0)
        ]),
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [230, 240, 255], textColor: [30, 30, 30], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });
      cursorY = doc.lastAutoTable?.finalY + 8 || cursorY + 20;
    }

    // Section 2 — Parking factur\u00e9s < 7j
    if (cursorY + 20 > pageH - 15) { doc.addPage(); cursorY = 20; }
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`\uD83C\uDD7F\uFE0F V\u00e9hicules au parking factur\u00e9s < 7 jours (${parking_factures.length})`, 14, cursorY);
    if (parking_factures.length > 0) {
      autoTable(doc, {
        startY: cursorY + 3,
        head: [['Immat', 'Marque', 'Client', 'N\u00b0 Facture', 'Date facture', 'Montant HT', 'Concession']],
        body: parking_factures.map(e => [
          e.identifiant || '\u2014', (e.marque || '\u2014').toUpperCase(), e.nom_client || '\u2014',
          e.num_facture || '\u2014', fmtDate(e.date_facture), fmtMontant(e.montant_ht), e.concession || '\u2014'
        ]),
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [220, 252, 231], textColor: [30, 30, 30], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });
      cursorY = doc.lastAutoTable?.finalY + 8 || cursorY + 20;
    } else {
      cursorY += 8;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text('Aucun v\u00e9hicule.', 14, cursorY);
      cursorY += 8;
    }

    // Section 3 — Parking avec demandes
    if (cursorY + 20 > pageH - 15) { doc.addPage(); cursorY = 20; }
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`\uD83D\uDCE9 V\u00e9hicules au parking avec demandes clients (${parking_demandes.length})`, 14, cursorY);
    if (parking_demandes.length > 0) {
      autoTable(doc, {
        startY: cursorY + 3,
        head: [['Immat', 'Marque', 'Client', 'All\u00e9e', 'Sujet', 'Commentaire', 'Responsable', 'Anciennet\u00e9']],
        body: parking_demandes.map(e => [
          e.identifiant || '\u2014', (e.marque || '\u2014').toUpperCase(), e.nom_client || '\u2014',
          e.allee || '\u2014', e.demande_sujet || '\u2014',
          (e.demande_commentaire || '\u2014').substring(0, 50),
          [e.demande_responsable_nom, e.demande_responsable_prenom].filter(Boolean).join(' ') || '\u2014',
          daysSince(e.created_at)
        ]),
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [254, 243, 199], textColor: [30, 30, 30], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });
    }

    // Pieds de page
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} / ${totalPages}`, pageW - 30, pageH - 8);
    }
    doc.save(`briefing_atelier_${today.replace(/\//g, '-')}.pdf`);
  };

  const thStyle = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '12px', background: 'var(--bg-muted)', color: 'var(--text-primary)', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 10px', fontSize: '12px', borderBottom: '1px solid var(--border-default)' };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Stats + bouton PDF */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="ai-lab-stats" style={{ margin: 0 }}>
          <div className="ai-lab-stat">
            <span className={`ai-lab-stat-value ${Number(stats.demandes_en_cours) > 0 ? 'warning' : ''}`}>{stats.demandes_en_cours || 0}</span>
            <span className="ai-lab-stat-label">Demandes en cours</span>
          </div>
          <div className="ai-lab-stat">
            <span className="ai-lab-stat-value info">{stats.interventions_jour || 0}</span>
            <span className="ai-lab-stat-label">Interventions du jour</span>
          </div>
          <div className="ai-lab-stat">
            <span className="ai-lab-stat-value">{stats.vehicules_parking || 0}</span>
            <span className="ai-lab-stat-label">V&#233;hicules parking</span>
          </div>
          <div className="ai-lab-stat">
            <span className={`ai-lab-stat-value ${Number(stats.parking_avec_demandes) > 0 ? 'danger' : ''}`}>{stats.parking_avec_demandes || 0}</span>
            <span className="ai-lab-stat-label">Parking + demandes</span>
          </div>
        </div>
        <button onClick={exportPDF} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Exporter PDF
        </button>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: 6 }}>Briefing du {dateStr}</div>

      {/* Section 1 — Demandes par collaborateur */}
      <h3 style={{ fontSize: 14, marginBottom: 8, marginTop: 16 }}>&#128172; Demandes clients &#224; traiter par collaborateur</h3>
      {demandesParResp.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucune demande client en cours.</p>}
      {demandesParResp.map(([resp, list]) => (
        <div key={resp} style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{resp} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({list.length})</span></h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>T&#233;l&#233;phone</th>
              <th style={thStyle}>Immat</th>
              <th style={thStyle}>Sujet</th>
              <th style={thStyle}>Commentaire</th>
              <th style={thStyle}>Date</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Appels</th>
            </tr></thead>
            <tbody>
              {list.map(d => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.client || '—'}</td>
                  <td style={tdStyle}>{d.telephone || '—'}</td>
                  <td style={tdStyle}><span className="plaque-badge">{d.immatriculation || '—'}</span></td>
                  <td style={tdStyle}>{d.sujet || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.commentaire_initial || '—'}</td>
                  <td style={tdStyle}>{fmtDate(d.date_demande)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>{d.nb_appels || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Section 2 — Parking factur&#233;s < 7j */}
      <h3 style={{ fontSize: 14, marginBottom: 8, marginTop: 24 }}>&#127359;&#65039; V&#233;hicules au parking factur&#233;s &lt; 7 jours <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({parking_factures.length})</span></h3>
      {parking_factures.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun v&#233;hicule.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thStyle}>Immat</th>
            <th style={thStyle}>Marque</th>
            <th style={thStyle}>Client</th>
            <th style={thStyle}>N&#176; Facture</th>
            <th style={thStyle}>Date facture</th>
            <th style={thStyle}>Montant HT</th>
            <th style={thStyle}>Concession</th>
          </tr></thead>
          <tbody>
            {parking_factures.map((e, i) => (
              <tr key={i}>
                <td style={tdStyle}><span className="plaque-badge">{e.identifiant || '—'}</span></td>
                <td style={tdStyle}>{(e.marque || '—').toUpperCase()}</td>
                <td style={tdStyle}>{e.nom_client || '—'}</td>
                <td style={tdStyle}>{e.num_facture || '—'}</td>
                <td style={tdStyle}>{fmtDate(e.date_facture)}</td>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--color-success)' }}>{fmtMontant(e.montant_ht)}</td>
                <td style={tdStyle}>{e.concession || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Section 3 — Parking avec demandes */}
      <h3 style={{ fontSize: 14, marginBottom: 8, marginTop: 24 }}>&#128233; V&#233;hicules au parking avec demandes clients <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({parking_demandes.length})</span></h3>
      {parking_demandes.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun v&#233;hicule.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thStyle}>Immat</th>
            <th style={thStyle}>Marque</th>
            <th style={thStyle}>Client</th>
            <th style={thStyle}>All&#233;e</th>
            <th style={thStyle}>Sujet</th>
            <th style={thStyle}>Commentaire</th>
            <th style={thStyle}>Responsable</th>
            <th style={thStyle}>Anciennet&#233;</th>
          </tr></thead>
          <tbody>
            {parking_demandes.map((e, i) => (
              <tr key={i}>
                <td style={tdStyle}><span className="plaque-badge">{e.identifiant || '—'}</span></td>
                <td style={tdStyle}>{(e.marque || '—').toUpperCase()}</td>
                <td style={tdStyle}>{e.nom_client || '—'}</td>
                <td style={tdStyle}>{e.allee || '—'}</td>
                <td style={tdStyle}>{e.demande_sujet || '—'}</td>
                <td style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.demande_commentaire || '—'}</td>
                <td style={tdStyle}>{[e.demande_responsable_nom, e.demande_responsable_prenom].filter(Boolean).join(' ') || '—'}</td>
                <td style={tdStyle}>{daysSince(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {generatedAt && (
        <div className="ai-lab-timestamp">
          G&#233;n&#233;r&#233; le {new Date(generatedAt).toLocaleString('fr-FR')}
        </div>
      )}
    </div>
  );
}

function AILab({ user, concessions = [] }) {
  // ── Briefing du matin ──
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingData, setBriefingData] = useState(null);
  const [briefingError, setBriefingError] = useState(null);
  const [selectedConcession, setSelectedConcession] = useState('');
  const [briefingType, setBriefingType] = useState('dg');

  // ── Suggestions IA ──
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsData, setSuggestionsData] = useState(null);
  const [suggestionsError, setSuggestionsError] = useState(null);

  const generateSuggestions = async () => {
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    try {
      const res = await aiLabApi.getSuggestions();
      setSuggestionsData(res.data);
    } catch (err) {
      setSuggestionsError(err.response?.data?.error || 'Erreur lors de la génération des suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const generateBriefing = async () => {
    setBriefingLoading(true);
    setBriefingError(null);
    try {
      const params = { type: briefingType };
      if (selectedConcession) params.concession_id = selectedConcession;
      const res = await aiLabApi.getBriefing(params);
      setBriefingData(res.data);
    } catch (err) {
      setBriefingError(err.response?.data?.error || 'Erreur lors de la génération du briefing');
    } finally {
      setBriefingLoading(false);
    }
  };

  // Markdown basique → HTML
  const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Lists
      .replace(/^\- (.+)$/gm, '<li>$1</li>')
      .replace(/^\* (.+)$/gm, '<li>$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Table rows
      .replace(/\|---.*\|/g, '')
      .replace(/^\|(.+)\|$/gm, (match, content) => {
        const cells = content.split('|').map(c => c.trim());
        return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      })
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');

    // Wrap loose <li> in <ul>
    html = html.replace(/(<li>.*?<\/li>)(?!\s*<li>)/gs, '<ul>$1</ul>');
    // Wrap loose <tr> in <table>
    html = html.replace(/(<tr>.*?<\/tr>(?:\s*<tr>.*?<\/tr>)*)/gs, '<table>$1</table>');

    return `<p>${html}</p>`;
  };

  return (
    <div className="ai-lab">
      <div className="ai-lab-grid">

        {/* ══════════ CARD: BRIEFING DU MATIN ══════════ */}
        <div className="ai-lab-card">
          <div className="ai-lab-card-header">
            <div className="ai-lab-card-title">
              <div className="card-icon">&#9728;&#65039;</div>
              <div>
                <h3>Briefing du matin</h3>
                <p className="ai-lab-card-desc">
                  Vue transversale : litiges, leads, demandes, planning, takata, recouvrement
                </p>
              </div>
            </div>
          </div>

          <div className="ai-lab-card-body">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={briefingType}
                onChange={e => setBriefingType(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
                  fontSize: '13px', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontWeight: 600
                }}
              >
                <option value="dg">Directeur G&#233;n&#233;ral</option>
                <option value="responsable-atelier">Responsable Atelier</option>
              </select>
              {concessions.length > 0 && (
                <select
                  value={selectedConcession}
                  onChange={e => setSelectedConcession(e.target.value)}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
                    fontSize: '13px', background: 'var(--bg-surface)', color: 'var(--text-primary)'
                  }}
                >
                  <option value="">Toutes les concessions</option>
                  {concessions.map(c => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              )}
              <button
                className="ai-lab-generate-btn"
                onClick={generateBriefing}
                disabled={briefingLoading}
              >
                {briefingLoading ? (
                  <>
                    <span className="ai-lab-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Analyse en cours...
                  </>
                ) : (
                  <>&#9889; G&eacute;n&eacute;rer le briefing</>
                )}
              </button>
            </div>

            {briefingError && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--color-error-light, #fef2f2)', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                {briefingError}
              </div>
            )}

            {briefingLoading && !briefingData && (
              <div className="ai-lab-loading">
                <div className="ai-lab-spinner" />
                <span className="ai-lab-loading-text">
                  Collecte des donn&eacute;es cross-modules et g&eacute;n&eacute;ration du briefing...
                </span>
              </div>
            )}

            {!briefingLoading && !briefingData && !briefingError && (
              <div className="ai-lab-empty">
                <div className="ai-lab-empty-icon">&#9889;</div>
                <p>Cliquez sur <strong>G&eacute;n&eacute;rer le briefing</strong> pour obtenir</p>
                <p>votre synth&egrave;se du matin bas&eacute;e sur les donn&eacute;es en temps r&eacute;el.</p>
              </div>
            )}

            {briefingData && briefingData.type !== 'responsable-atelier' && (
              <>
                {briefingData.data?.stats && (
                  <div className="ai-lab-stats">
                    <div className="ai-lab-stat">
                      <span className={`ai-lab-stat-value ${Number(briefingData.data.stats.litiges_en_retard) > 0 ? 'danger' : ''}`}>
                        {briefingData.data.stats.litiges_en_retard}
                      </span>
                      <span className="ai-lab-stat-label">Litiges en retard</span>
                    </div>
                    <div className="ai-lab-stat">
                      <span className="ai-lab-stat-value info">{briefingData.data.stats.litiges_actifs}</span>
                      <span className="ai-lab-stat-label">Litiges actifs</span>
                    </div>
                    <div className="ai-lab-stat">
                      <span className={`ai-lab-stat-value ${Number(briefingData.data.stats.leads_a_traiter) > 0 ? 'warning' : ''}`}>
                        {briefingData.data.stats.leads_a_traiter}
                      </span>
                      <span className="ai-lab-stat-label">Leads &#224; traiter</span>
                    </div>
                    <div className="ai-lab-stat">
                      <span className="ai-lab-stat-value">{briefingData.data.stats.demandes_en_cours}</span>
                      <span className="ai-lab-stat-label">Demandes en cours</span>
                    </div>
                    <div className="ai-lab-stat">
                      <span className="ai-lab-stat-value info">{briefingData.data.stats.interventions_jour}</span>
                      <span className="ai-lab-stat-label">Interventions du jour</span>
                    </div>
                  </div>
                )}
                <div
                  className="ai-lab-briefing"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(briefingData.briefing) }}
                />
                {briefingData.generated_at && (
                  <div className="ai-lab-timestamp">
                    G&eacute;n&eacute;r&eacute; le {new Date(briefingData.generated_at).toLocaleString('fr-FR')}
                  </div>
                )}
              </>
            )}

            {briefingData && briefingData.type === 'responsable-atelier' && (
              <AtelierBriefingView data={briefingData.data} generatedAt={briefingData.generated_at} />
            )}
          </div>
        </div>

        {/* ══════════ CARD: SUGGESTIONS IA ══════════ */}
        <div className="ai-lab-card">
          <div className="ai-lab-card-header">
            <div className="ai-lab-card-title">
              <div className="card-icon">&#128161;</div>
              <div>
                <h3>Suggestions IA</h3>
                <p className="ai-lab-card-desc">
                  Analyse les donn&eacute;es r&eacute;elles et les jonctions cross-modules pour proposer des features &agrave; fort ROI
                </p>
              </div>
            </div>
          </div>

          <div className="ai-lab-card-body">
            <button
              className="ai-lab-generate-btn"
              onClick={generateSuggestions}
              disabled={suggestionsLoading}
            >
              {suggestionsLoading ? (
                <>
                  <span className="ai-lab-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Analyse en cours...
                </>
              ) : (
                <>&#128161; Analyser et sugg&eacute;rer</>
              )}
            </button>

            {suggestionsError && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--color-error-light, #fef2f2)', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                {suggestionsError}
              </div>
            )}

            {suggestionsLoading && !suggestionsData && (
              <div className="ai-lab-loading">
                <div className="ai-lab-spinner" />
                <span className="ai-lab-loading-text">
                  Collecte des m&eacute;triques, analyse des jonctions cross-modules...
                </span>
              </div>
            )}

            {!suggestionsLoading && !suggestionsData && !suggestionsError && (
              <div className="ai-lab-empty">
                <div className="ai-lab-empty-icon">&#128161;</div>
                <p>L'IA analyse vos donn&eacute;es r&eacute;elles, croise les modules</p>
                <p>et propose des features concr&egrave;tes bas&eacute;es sur votre activit&eacute;.</p>
              </div>
            )}

            {suggestionsData && (
              <>
                {/* Métriques cross-modules */}
                {suggestionsData.cross_data && (
                  <div className="ai-lab-stats" style={{ marginTop: 16 }}>
                    {Number(suggestionsData.cross_data.demandes_avec_litige_meme_client) > 0 && (
                      <div className="ai-lab-stat">
                        <span className="ai-lab-stat-value danger">{suggestionsData.cross_data.demandes_avec_litige_meme_client}</span>
                        <span className="ai-lab-stat-label">Demandes + Litige</span>
                      </div>
                    )}
                    {Number(suggestionsData.cross_data.leads_avec_vo_en_stock) > 0 && (
                      <div className="ai-lab-stat">
                        <span className="ai-lab-stat-value info">{suggestionsData.cross_data.leads_avec_vo_en_stock}</span>
                        <span className="ai-lab-stat-label">Leads matchant un VO</span>
                      </div>
                    )}
                    {Number(suggestionsData.cross_data.parking_vehicules_takata) > 0 && (
                      <div className="ai-lab-stat">
                        <span className="ai-lab-stat-value warning">{suggestionsData.cross_data.parking_vehicules_takata}</span>
                        <span className="ai-lab-stat-label">Parking + Takata</span>
                      </div>
                    )}
                    {Number(suggestionsData.cross_data.receptions_sans_planning) > 0 && (
                      <div className="ai-lab-stat">
                        <span className="ai-lab-stat-value warning">{suggestionsData.cross_data.receptions_sans_planning}</span>
                        <span className="ai-lab-stat-label">R&eacute;ceptions sans planning</span>
                      </div>
                    )}
                    {Number(suggestionsData.cross_data.recouvrement_client_avec_demande) > 0 && (
                      <div className="ai-lab-stat">
                        <span className="ai-lab-stat-value danger">{suggestionsData.cross_data.recouvrement_client_avec_demande}</span>
                        <span className="ai-lab-stat-label">Recouvrement + Demande</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Suggestions markdown */}
                <div
                  className="ai-lab-briefing"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(suggestionsData.suggestions) }}
                />

                {suggestionsData.generated_at && (
                  <div className="ai-lab-timestamp">
                    Analyse du {new Date(suggestionsData.generated_at).toLocaleString('fr-FR')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AILab;
